// FUNCTIONALITY-101 — Store V2 checkout client.
// Calls SAME-ORIGIN Pages Function only. Never a cross-origin Worker fetch.

import type { CartV2Item } from "./cart-v2.js";
import { waitForTelegramInitData } from "../lib/telegram.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import { fetchWithTimeout, isFetchTimeout } from "./fetch-timeout-v2.js";
import { parseBankPaymentPayload, type BankPaymentClientPayload } from "./bank-iban-payment-v2.js";
// BANK-IBAN-R3.6 — payment purpose / instruction exist only after STG create.
// ORDER_AMENDMENT later must revoke+reissue; never silently mutate amount.

const CHECKOUT_URL = "/api/store-v2/checkout";
/** Cart+draft+place chain — longer than default 8s cabinet timeout. */
const CHECKOUT_FETCH_TIMEOUT_MS = 45_000;

export interface CheckoutV2Input {
  sessionKey: string;
  items: CartV2Item[];
  promoCode?: string | null | undefined;
  contactName?: string | undefined;
  contactPhone?: string | undefined;
  deliveryCity?: string | undefined;
  deliveryWarehouse?: string | undefined;
  note?: string | undefined;
  paymentMethod?: string | undefined;
  /** CUSTOMER-R4.7-A — when bank pay is expected (not a second payment rail). */
  paymentTiming?: "immediate" | "after_manager_confirmation" | null | undefined;
  /** R5A — stable key so warm handoff reuses one order per checkout fingerprint. */
  idempotencyKey?: string | null | undefined;
  novaPoshtaDelivery?: Record<string, unknown> | null | undefined;
  novaPoshtaManualFallback?: boolean | undefined;
  /** CUSTOMER-R4.15-K18 */
  availabilityReviewRequired?: boolean | undefined;
  availabilityLineReasons?: string | null | undefined;
  /** CUSTOMER-R4.7 — source public STG only. Never an internal UUID. */
  reorderFromPublicOrderNumber?: string | null | undefined;
  /** BUYER-INFRA-R1 — optional APPROVED business buyer; omit = retail CONSUMER. */
  buyerProfileId?: string | null | undefined;
  /** BUYER-INFRA-R1 — optional saved server delivery profile (independent of buyer). */
  deliveryProfileId?: string | null | undefined;
}

export interface CheckoutV2Result {
  ok: boolean;
  orderId?: string | undefined;
  displayOrderNumber?: string | undefined;
  status?: string | undefined;
  subtotalUah?: number | undefined;
  discountUah?: number | undefined;
  totalUah?: number | undefined;
  bankPayment?: BankPaymentClientPayload | null | undefined;
  paymentTiming?: "immediate" | "after_manager_confirmation" | undefined;
  error?: string | undefined;
  message?: string | undefined;
  messageEn?: string | undefined;
  retryable?: boolean | undefined;
  productId?: string | undefined;
  trustedUnitPriceUah?: number | undefined;
  clientUnitPriceUah?: number | undefined;
}

function randomSession(): string {
  const rnd = Math.random().toString(36).slice(2, 10);
  return `storev2-${Date.now()}-${rnd}`;
}

/** Display-only hint — never used as auth SoT (Worker verifies initData HMAC). */
function readTelegramUserId(): string | null {
  try {
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number | string } } } };
    }).Telegram?.WebApp;
    const id = tg?.initDataUnsafe?.user?.id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

function readReferralCode(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const direct = params.get("ref");
    const start = params.get("tgWebAppStartParam") ?? params.get("start_param");
    const raw = direct || (start?.startsWith("ref_") ? start.slice(4) : "");
    return /^REF-[A-Z0-9]{4,20}$/i.test(raw ?? "") ? String(raw).toUpperCase() : null;
  } catch {
    return null;
  }
}

export async function submitCheckoutV2(input: CheckoutV2Input): Promise<CheckoutV2Result> {
  // COMMERCE-CANARY-163A-R2B — wait for WebApp.initData before posting; never rely on body user id.
  const initData = await waitForTelegramInitData(2500);
  if (!initData) {
    return {
      ok: false,
      error: "authentication_required",
      message: "Не вдалося підтвердити сесію Telegram. Закрийте та знову відкрийте магазин.",
      retryable: true,
    };
  }

  const promoCode = (input.promoCode ?? "").trim().toUpperCase();
  const body = {
    session_key: input.sessionKey || randomSession(),
    coupon_code: promoCode || null,
    // Legacy flag kept for backward-compatible proxy handling.
    apply_tg50: promoCode === "TG50",
    // Not identity SoT — Worker ignores this for binding; kept for legacy logging only.
    telegram_user_id: readTelegramUserId(),
    referral_code: readReferralCode(),
    contact_name: input.contactName ?? null,
    contact_phone: input.contactPhone ?? null,
    recipient_name: input.contactName ?? null,
    recipient_phone: input.contactPhone ?? null,
    delivery_method: "nova_poshta",
    delivery_city: input.deliveryCity ?? null,
    delivery_warehouse: input.deliveryWarehouse ?? null,
    note: input.note ?? null,
    payment_method: input.paymentMethod ?? null,
    payment_timing: input.paymentTiming ?? null,
    idempotency_key: input.idempotencyKey ?? null,
    nova_poshta_delivery: input.novaPoshtaDelivery ?? null,
    nova_poshta_manual_fallback: input.novaPoshtaManualFallback === true,
    availability_review_required: input.availabilityReviewRequired === true,
    availability_line_reasons: input.availabilityLineReasons ?? null,
    reorder_from_public_order_number: input.reorderFromPublicOrderNumber ?? null,
    buyer_profile_id: input.buyerProfileId ?? null,
    delivery_profile_id: input.deliveryProfileId ?? null,
    items: input.items.map((i) => ({
      product_id: i.id,
      title: i.title,
      qty: i.qty,
      unit_price_uah: i.priceUah,
      price_resolved: true,
      stock_status: i.stockStatus ?? "unknown",
      image_url: i.imageUrl ?? null,
      // Store article only — never Prom id / barcode echo.
      store_sku: i.sku && i.sku !== i.id ? i.sku : null,
      sku: i.sku && i.sku !== i.id ? i.sku : null,
    })),
  };

  let res: Response;
  try {
    // COMMERCE-LAUNCH-167A-R2 — bounded fetch; retry keeps same idempotency_key → same order.
    res = await fetchWithTimeout(
      CHECKOUT_URL,
      {
        method: "POST",
        headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      },
      CHECKOUT_FETCH_TIMEOUT_MS,
    );
  } catch (err) {
    const timeout = isFetchTimeout(err);
    return {
      ok: false,
      error: timeout ? "timeout" : "network",
      message: timeout
        ? "Сервер відповідає довше, ніж зазвичай. Якщо замовлення вже створилось — натисніть ще раз (те саме замовлення)."
        : "Не вдалося звʼязатися з сервером. Спробуйте ще раз.",
      retryable: true,
    };
  }

  let json: Record<string, unknown> = {};
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    /* non-JSON */
  }

  if (!res.ok || json.ok === false) {
    const err = String(json.error ?? `http_${res.status}`);
    const retryable =
      json.retryable === true ||
      err === "catalog_revalidation_unavailable" ||
      err === "catalog_snapshot_unavailable" ||
      err === "nova_poshta_provider_unavailable";
    const mappedMessage =
      typeof json.message === "string" && json.message.trim() && err !== "internal_server_error"
        ? json.message
        : err === "price_changed"
          ? "Ціна змінилася. Оновіть кошик і перевірте нову суму перед оплатою."
          : err === "product_not_sellable"
            ? String(json.message ?? "Товару немає в наявності або він більше не продається.")
            : err === "authentication_required" || err === "invalid_authentication" || res.status === 401
              ? "Не вдалося підтвердити сесію. Закрийте та знову відкрийте магазин."
              : err === "payment_config_unavailable"
                ? "Оплата зараз недоступна. Спробуйте ще раз або напишіть менеджеру."
              : err === "nova_poshta_settlement_required" ||
                  err === "nova_poshta_warehouse_required" ||
                  err === "validation_failed"
                ? "Оберіть місто та відділення Нової пошти."
                : err === "nova_poshta_warehouse_inactive"
                  ? "Це відділення більше недоступне. Оберіть інше."
                  : res.status === 405 || err === "http_405"
                    ? "Сервіс оформлення тимчасово недоступний. Спробуйте пізніше."
                    : "Не вдалося створити замовлення. Товари й дані збережено — спробуйте ще раз.";
    return {
      ok: false,
      error: err,
      message: mappedMessage,
      messageEn: typeof json.message_en === "string" ? json.message_en : undefined,
      retryable,
      productId: typeof json.product_id === "string" ? json.product_id : undefined,
      trustedUnitPriceUah:
        json.trusted_unit_price_uah != null ? Number(json.trusted_unit_price_uah) : undefined,
      clientUnitPriceUah:
        json.client_unit_price_uah != null ? Number(json.client_unit_price_uah) : undefined,
    };
  }

  const timingRaw = String(json.payment_timing ?? "").trim().toLowerCase();
  const paymentTiming =
    timingRaw === "after_manager_confirmation" ? ("after_manager_confirmation" as const) : ("immediate" as const);

  return {
    ok: true,
    orderId: json.order_id ? String(json.order_id) : undefined,
    displayOrderNumber: json.display_order_number ? String(json.display_order_number) : undefined,
    status: json.status ? String(json.status) : undefined,
    subtotalUah: json.subtotal_uah != null ? Number(json.subtotal_uah) : undefined,
    discountUah: json.discount_uah != null ? Number(json.discount_uah) : undefined,
    totalUah: json.total_uah != null ? Number(json.total_uah) : undefined,
    bankPayment: parseBankPaymentPayload(json.bank_payment),
    paymentTiming,
  };
}
